import {
  Button,
  PanelActions,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Toggle,
  Tooltip,
} from '@customerio/pluma-components/react';
import { Controller, useFormContext, useWatch } from '@/components/form';

type IntegrationStatePanelProps = {
  connectorName: string;
  isFormDisabled: boolean;
  isRemoving: boolean;
  canRemove: boolean;
  onRemove: () => Promise<void>;
};

/**
 * The enable toggle and disconnect control, shown only once the connector exists — on first connect
 * there is nothing to disable or remove, and selecting events is what turns it on.
 */
export function IntegrationStatePanel({
  connectorName,
  isFormDisabled,
  isRemoving,
  canRemove,
  onRemove,
}: IntegrationStatePanelProps) {
  const { control } = useFormContext();
  const events = useWatch({ control, name: 'events' });
  const enabled = useWatch({ control, name: 'enabled' });

  // A connector with no events selected cannot be enabled — it would forward nothing. An already
  // enabled connector keeps its toggle live so it can be switched off.
  const cannotEnable = enabled !== true && (!Array.isArray(events) || events.length === 0);

  return (
    <PanelHeader>
      <PanelTitle>Integration state</PanelTitle>
      <PanelDescription>
        Note: If you disable, your integration settings will be saved, but no events will be sent.
      </PanelDescription>

      <PanelActions alignItems="center">
        <Tooltip isDisabled={!cannotEnable} content="To enable, you must select at least one event to send.">
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <Toggle
                ariaLabel="Integration State"
                size="sm"
                isChecked={field.value === true}
                isDisabled={isFormDisabled || cannotEnable}
                onChange={(event) => {
                  field.onChange(event.currentTarget.checked);
                }}
              />
            )}
          />
        </Tooltip>

        <Button
          variant="secondary"
          isDanger
          // `!canRemove` is its own gate: deleting needs `journeys/exports/delete`, which an
          // edit-capable role need not hold.
          isDisabled={isFormDisabled || isRemoving || !canRemove}
          isLoading={isRemoving}
          // Not `onClick={onRemove}`: Pluma auto-loads a button whose handler returns a pending
          // promise, which would spin the trigger for as long as the confirmation modal is open,
          // before anything is being removed. `isRemoving` covers the real request.
          onClick={() => {
            void onRemove();
          }}
          aria-label={`Remove ${connectorName} integration`}
        >
          {isRemoving ? 'Removing…' : 'Remove integration'}
        </Button>
      </PanelActions>
    </PanelHeader>
  );
}
